import type { EmbeddedCheckout } from './checkout';
import { isEmbeddedClientSecret } from './checkout';
import { abortable, paymentDelay } from '../lib/embedded-payment';

export type ServicePaymentResult = {
  state: 'pending' | 'confirmed' | 'review' | 'expired' | 'refunded' | 'revoked';
  title: string;
  message: string;
};
const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;

/** Service checkout uses the same secure SDK, without inventing a marketplace order. */
export function normalizeServiceCheckout(value: unknown, now = Date.now()): EmbeddedCheckout {
  const checkout = record(value);
  const sessionMode = typeof checkout?.sessionId === 'string' ? /^cs_(test|live)_[A-Za-z0-9_]{5,200}$/.exec(checkout.sessionId)?.[1] : null;
  const keyMode = typeof checkout?.publishableKey === 'string' ? /^pk_(test|live)_[A-Za-z0-9]{16,240}$/.exec(checkout.publishableKey)?.[1] : null;
  if (!checkout || checkout.provider !== 'stripe' || checkout.uiMode !== 'embedded' || !sessionMode || sessionMode !== keyMode
    || typeof checkout.sessionId !== 'string' || !isEmbeddedClientSecret(checkout.clientSecret, checkout.sessionId)
    || typeof checkout.publishableKey !== 'string' || typeof checkout.expiresAt !== 'string'
    || !Number.isFinite(Date.parse(checkout.expiresAt)) || Date.parse(checkout.expiresAt) <= now
    || typeof checkout.klarnaEligible !== 'boolean') throw new Error('Il pagamento integrato non è valido o è scaduto. Verifica lo stato prima di riprovare.');
  return { provider: 'stripe', uiMode: 'embedded', sessionId: checkout.sessionId, clientSecret: checkout.clientSecret,
    publishableKey: checkout.publishableKey, expiresAt: checkout.expiresAt, klarnaEligible: checkout.klarnaEligible };
}

/** Completion in Stripe's frame only starts authenticated server polling. */
export async function verifyServicePayment(options: {
  read: (signal: AbortSignal) => Promise<ServicePaymentResult>;
  signal: AbortSignal; attempts?: number; delayMs?: number;
  onResult?: (result: ServicePaymentResult) => void;
}): Promise<ServicePaymentResult> {
  const attempts = Number.isFinite(options.attempts ?? 8) ? Math.min(10, Math.max(1, Math.floor(options.attempts ?? 8))) : 8;
  let latest: ServicePaymentResult | undefined;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (options.signal.aborted) throw new DOMException('Operazione annullata', 'AbortError');
    latest = await abortable(options.read(options.signal), options.signal);
    if (options.signal.aborted) throw new DOMException('Operazione annullata', 'AbortError');
    if (!latest || !['pending','confirmed','review','expired','refunded','revoked'].includes(latest.state)
      || typeof latest.title !== 'string' || typeof latest.message !== 'string') throw new Error('Lo stato del pagamento non è verificabile.');
    options.onResult?.(latest);
    if (latest.state !== 'pending') return latest;
    if (attempt < attempts - 1) await paymentDelay(options.delayMs ?? 2500, options.signal);
  }
  return latest!;
}
